<template>
  <div class="ota-device-format">
    <div class="format-content">
      <div class="header">
        Format Disk
      </div>
      <br>

      <div
        v-if="!loading"
        class="storage-info"
      >
        <div v-if="!formatted" class="content-section">
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="selectedDisk"
                :items="storageDevices"
                item-text="label"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4" class="format-label">Storage Class:</v-col>
            <v-col cols="8">{{ storageClass }}</v-col>
          </v-row>

          <v-row>
            <v-col cols="4" class="format-label">Model:</v-col>
            <v-col cols="8">{{ model }}</v-col>
          </v-row>

          <v-row>
            <v-col cols="4" class="format-label">Revision:</v-col>
            <v-col cols="8">{{ revision }}</v-col>
          </v-row>

          <v-row>
            <v-col cols="4" class="format-label">Capacity/(Mb):</v-col>
            <v-col cols="8">{{ capacity }}</v-col>
          </v-row>

          <br>

          <v-row class="actions">
            <v-btn 
              text
              @click="close"
            >
              CANCEL
            </v-btn>
            <v-spacer></v-spacer>

            <v-dialog
              v-model="dialog"
              persistent
              width="500"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  v-bind="attrs"
                  v-on="on"
                >
                  FORMAT
                </v-btn>
              </template>

              <v-card class="confirm-format">
                <v-row  
                  align="center"
                  justify="center"
                  class="padding-top"
                >
                  <v-col cols="3" class="d-flex justify-center">
                    <v-icon 
                      color="red"
                      size="50"
                    >
                      mdi-alert
                    </v-icon>
                  </v-col>

                  <v-col class="d-flex justify-center">
                    All data on the disk will be permanently lost and the device will be rebooted. Are you sure you want to proceed?
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    Enter 'format' below to confirm.
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="confirmationInput"
                    />
                  </v-col>
                </v-row>

                <v-row  
                  align="center"
                  justify="center"
                  class="padding-bottom reboot-actions"
                >
                  <v-btn
                    color="red" 
                    text
                    @click="dialog = !dialog"
                  >
                    No
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn 
                    v-if="!formatRequestState"
                    :disabled="userConfirmation"
                    color="green"
                    text
                    @click="submitFormat"
                  >
                    Yes
                  </v-btn>
                  <v-progress-circular
                    v-else
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                  <br>
                </v-row>
              </v-card>
            </v-dialog>
          </v-row>

        </div>

        <div v-if="formatted && status">
          <!-- Device formatted successfully -->
          <v-row  
            align="center"
            justify="center"
            class="padding-top"
          >
            <v-col cols="3" class="d-flex justify-center">
              <v-icon 
                color="green"
                size="50"
              >
                mdi-check
              </v-icon>
            </v-col>

            <v-col class="d-flex justify-center">
              The format request succeeded.
            </v-col>
          </v-row>

          <v-row
            align="center"
            justify="center"
          >
            <v-btn 
              text
              @click="close"
            >
              OK
            </v-btn>
          </v-row>
          <br>
        </div>

        <div v-if="formatted && !status">
          <!-- Device Formatting failed -->
          <v-row  
            align="center"
            justify="center"
            class="padding-top"
          >
            <v-col cols="3" class="d-flex justify-center">
              <v-icon 
                color="red"
                size="50"
              >
                mdi-alert
              </v-icon>
            </v-col>

            <v-col class="d-flex justify-center">
              {{ errorText }}
            </v-col>
          </v-row>

          <v-row
            align="center"
            justify="center"
          >
            <v-btn 
              text
              @click="close"
            >
              OK
            </v-btn>
          </v-row>
          <br>
        </div>
      </div>

      <div v-if="loading" class="format-device-loading">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      
    </div>    
  </div>
</template>

<script>

import { GetOrgDeviceInfo, FormatDisk } from '@/services/API.js';

import { Logger } from "aws-amplify";
const logger = new Logger("format device");

export default {
  name: 'DeviceFormat',
  props: {
    org: {
      type: String,
      required: true
    },
    device: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      storageDevices: [],
      selectedDisk: null,
      loading: Boolean,
      formatRequestState: false,
      dialog: false,
      confirmationInput: '',
      formatted: false,
      status: Boolean,
      errorText: ''
    }
  },
  computed: {
    userConfirmation() {
      return this.confirmationInput.toLowerCase() == 'format' ? false : true;
    },
    deviceID() {
      let device = this.device.devices.filter(item => {
        return item.is_defaultdevice === true || item.is_default_device === true || item.is_default_device === "true";
      })
      return device[0].device_id;
    },
    deviceCount() {
      return this.device.devices.length;
    },
    selectedDiskInfo() {
      let disk = this.storageDevices.find(device => {
        return device.label == this.selectedDisk;
      })
      return disk;
    },
    storageClass() {
      return this.selectedDiskInfo ? this.selectedDiskInfo.class : '';
    },
    model() {
      return this.selectedDiskInfo ? this.selectedDiskInfo.model : '';
    },
    revision() {
      return this.selectedDiskInfo ? this.selectedDiskInfo.revision : '';
    },
    capacity() {
      return this.selectedDiskInfo ? this.selectedDiskInfo.capacity : '';
    }
  },
  mounted() {
    this.fetchDeviceInfo();
  },
  methods: {
    submitFormat() {
      this.formatRequestState = true;

      let payload = {
        storageID: this.selectedDiskInfo.id
      }

      FormatDisk(this.deviceID, payload)
      .then(res => {
        logger.info('res here : ', res);
        let status = res.result.status;
        
        if (status == '200') {
          this.formatRequestState = false;
          this.formatted = true;
          this.status = true;
          this.dialog = !this.dialog;
        }
        else {
          this.formatRequestState = false;
          this.formatted = true;
          this.status = false;
          this.dialog = !this.dialog;

          this.handleError(status);
        }
      })
      .catch(err => {
        this.formatRequestState = false;
        this.formatted = true;
        this.status = false;
        this.dialog = !this.dialog;

        this.errorText = 'An error occurred.';
        logger.info('err : ', err);
      });
    },
    handleError(code) {
      let map = {
        '409': 'Error 409: The specified disk is not present or unavailable.',
        '406': 'Error 406: The specified disk does not exist.',
        '400': 'Error 400: Bad request.'
      };

      // eslint-disable-next-line no-prototype-builtins
      if (map.hasOwnProperty(code)) {
        this.errorText = map[code];
      }
      else {
        this.errorText = 'An error has occurred.'
      }
    },
    close() {
      this.$emit('close');
    },
    fetchDeviceInfo() {
      this.loading = true;

      GetOrgDeviceInfo(this.org, this.deviceID)
      .then(res => {
        let data = res.components;

        this.parseStorageInfo(data);

        // Initialise selected disk
        this.selectedDisk = this.storageDevices[0].label;

        this.loading = false;
      })
      .catch(err => {
        logger.info(err);
        this.loading = false;
      });
    },
    storagePropMap(item, medium) {
      let mediumMap = {
        'sd': '(SD Card)',
        'hd': '(HDD)',
        'hdd': '(HDD)'
      }

      let map = {
        'primary': `Disk 1 ${mediumMap[medium]}`,
        'secondary': `Disk 2 ${mediumMap[medium]}`
      }
      logger.info('storagePropMap', item, map)
      // eslint-disable-next-line no-prototype-builtins
      if (map.hasOwnProperty(item)) {
        return map[item]
      }
    },
    parseStorageInfo(data) {
      let diskInfo = data.find(item => {
        return item.type == 'storage'
      });
      
      // Add prop for display label
      diskInfo.devices.forEach((item) => {
        item.label = this.storagePropMap(item.class, item.medium);
      })

      this.storageDevices = diskInfo.devices;
    }
  }
}
</script>

<style lang="scss" scoped>
  .ota-device-format {
    z-index: 9998;
    position: fixed;
    left: 0%;
    right: 0;
    top: 0;
    background-color: rgba(0, 0, 0, 0.4) !important;
    width: 100vw;
    height: 100vh;

    .format-content {
      background: white;
      margin: 0;
      position: relative;
      top: 50%;
      left: 50%;
      -ms-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
      padding: 0;
      overflow: hidden;
      border: solid grey 2px;
      width: 500px;

      .header {
        background: #01ADB6;
        color: white;
        padding: .5em;
        font-weight: 400;
        font-size: 20px;
      }

      .storage-info {
        padding: 0 1em;
      }

      .format-label {
        color: #01ADB6;
      }

      .format-device-loading {
        text-align: center;
        padding: 2em;
      }

      .actions {
        padding-left: .5em;
        padding-right: .5em;
        padding-bottom: 1em;
      }
  } 
    .padding-top {
      padding-top: 1em;
    }

    .padding-bottom {
      padding-bottom: 1em;
    }
  }

  .confirm-format {
    padding: 1.5em;
  }
</style>